import { HorizontalScoreboardNavigatorTypes } from "./HorizontalScoreboardNavigator/HorizontalScoreboardNavigator";
import { addDays, format, isValid, parse, subDays } from "date-fns";
import { enUS } from "date-fns/locale";
import { getCultureLocalizer } from "../shared/utils";

const datePositionTypes = {
  from: "from",
  to: "to",
};

const FULL_SCHEDULE_DEFAULT_WIDTH = 176;
export const SCROLL_DEBOUNCE_TIME = 300;
const ELEMENT_GAP = 16;

const getDateOrDefault = (dateString, dateDirectionType = null) => {
  if (!dateString) {
    const now = Date.now();
    const sixDaysAgo = subDays(now, 10);
    const sixDaysLater = addDays(now, 10);
    const dateObj =
      dateDirectionType === datePositionTypes.from
        ? { date: sixDaysAgo, dateString: format(sixDaysAgo, "yyyy-MM-dd") }
        : {
          date: sixDaysLater,
          dateString: format(sixDaysLater, "yyyy-MM-dd"),
        };

    return dateObj;
  }

  return {
    date: parse(dateString, "yyyy-MM-dd", new Date()),
    dateString,
  };
};

export const getMatchInitialDate = (dateFrom, dateTo) => {
  const from = getDateOrDefault(dateFrom, datePositionTypes.from);
  const to = getDateOrDefault(dateTo, datePositionTypes.to);
  const formattedDateString =
    from.dateString === to.dateString
      ? format(from.date, "PPPP", { locale: getCultureLocalizer() })
      : `${format(from.date, "PPPP", {
        locale: getCultureLocalizer(),
      })} - ${format(to.date, "PPPP", { locale: getCultureLocalizer() })}`;

  return { from: from.dateString, to: to.dateString, formattedDateString };
};

export const handleInitialPosition = ({ scoreboardRef }) => {
  const currentScoreboardRef = scoreboardRef?.current;

  if (currentScoreboardRef) {
    currentScoreboardRef.scroll({ left: 1920 });
    currentScoreboardRef.scrollLeft = 1920;
  }
};

export const handleNavigators = ({
  navigatorLeftRef,
  navigatorRightRef,
  scoreboardRef,
}) => {
  if (!scoreboardRef?.current) {
    return;
  }

  const scoreboard = scoreboardRef?.current;

  const fullScheduleButtonWidth = getScheduleButtonWidth(scoreboard);
  const leftPosition = scoreboard.scrollLeft;
  const scrollWidth = scoreboard.scrollWidth;
  const wrapperWidth = scoreboard.offsetWidth;

  const hideLeftArrow = leftPosition < fullScheduleButtonWidth;
  const hideRightArrow =
    leftPosition + ELEMENT_GAP >= scrollWidth - wrapperWidth;

  const currentNavigatorLeftRef = navigatorLeftRef?.current;
  const currentNavigatorRightRef = navigatorRightRef?.current;

  if (hideLeftArrow) {
    currentNavigatorLeftRef.classList.remove("--show");
  } else {
    currentNavigatorLeftRef.classList.add("--show");
  }

  if (hideRightArrow) {
    currentNavigatorRightRef.classList.remove("--show");
  } else {
    currentNavigatorRightRef.classList.add("--show");
  }
};

export const moveHorizontalScoreboard = ({
  e,
  position,
  scoreboardRef,
  navigatorLeftRef,
  navigatorRightRef,
}) => {
  e.preventDefault();
  const currentRef = scoreboardRef?.current;
  const currentNavigatorLeftRef = navigatorLeftRef?.current;
  const currentNavigatorRightRef = navigatorRightRef?.current;

  if (currentRef?.scrollLeft >= 0) {
    currentNavigatorLeftRef.classList.add("--show");
  }

  if (
    (position !== HorizontalScoreboardNavigatorTypes.left &&
      position !== HorizontalScoreboardNavigatorTypes.right) ||
    !currentRef
  )
    return null;

  if (currentNavigatorLeftRef && currentNavigatorRightRef) {
    currentNavigatorLeftRef.style.pointerEvents = "none";
    currentNavigatorRightRef.style.pointerEvents = "none";
  }

  const wrapperWidth = currentRef?.offsetWidth;

  const nextLeft =
    position === HorizontalScoreboardNavigatorTypes.left
      ? currentRef.scrollLeft - wrapperWidth
      : currentRef.scrollLeft + wrapperWidth;

  const nextPosition = getNextScrollPosition({
    currentRef,
    position,
    nextLeft,
  });

  if (nextPosition) {
    const fullScheduleButtonWidth = getScheduleButtonWidth(currentRef);
    const left =
      nextPosition > fullScheduleButtonWidth + ELEMENT_GAP ? nextPosition : 0;
    currentRef.scroll({
      left,
      behavior: "smooth",
    });
  }

  if (currentNavigatorLeftRef && currentNavigatorRightRef) {
    currentNavigatorLeftRef.style.pointerEvents = "auto";
    currentNavigatorRightRef.style.pointerEvents = "auto";
  }
};

function getScheduleButtonWidth(currentRef) {
  const fullScheduleButtons = currentRef.querySelectorAll(
    ".mls-c-horizontal-scoreboard__full-schedule"
  );

  const fullScheduleButton = Array.isArray(fullScheduleButtons)
    ? fullScheduleButtons[0]
    : null;

  const fullScheduleButtonWidth =
    fullScheduleButton?.offsetWidth || FULL_SCHEDULE_DEFAULT_WIDTH;

  return fullScheduleButtonWidth;
}

function getNextScrollPosition({ currentRef, position, nextLeft }) {
  const elements = currentRef.querySelectorAll(".mls-c-horizontal-match");
  let nextPosition = null;

  for (let index = 0; index < elements.length; index++) {
    const element = elements[index];
    const elementLeftPosition =
      position === HorizontalScoreboardNavigatorTypes.left
        ? element.offsetLeft
        : element.offsetLeft + element.offsetWidth;

    if (elementLeftPosition > nextLeft) {
      nextPosition = element.offsetLeft;
      break;
    }
  }

  return nextPosition;
}

export const getDateTranslation = (date, t) => {
  if (!date || !t) return null;

  const dayObj = new Date(date);

  if (isValid(dayObj)) {
    const weekdayLabelFormat = format(dayObj, "cccc", {
      locale: getCultureLocalizer(),
    })?.toLowerCase();
    const dayLabelFormat = format(dayObj, "d");
    const monthLabelFormat = format(dayObj, "MMMM", {
      locale: getCultureLocalizer(),
    })?.toLowerCase();

    if (getCultureLocalizer() !== enUS) {
      return `${t(weekdayLabelFormat)} ${dayLabelFormat} ${t(
        monthLabelFormat
      )}`;
    }

    if (getCultureLocalizer() == enUS) {
      return `${t(weekdayLabelFormat)} ${t(
        monthLabelFormat
      )} ${dayLabelFormat}`;
    }
  } else {
    return date;
  }
};

export function formatStatsAPIMatches(matches) {
  return matches?.map((match) => {
    return {
      ...match,
      match_information: {
        ...match,
        kickoff_time: match?.planned_kickoff_time,
        competition: formatStatsAPICompetition(match),
      },
      home: formatStatsAPIMatch({ match, role: "home" }),
      away: formatStatsAPIMatch({ match, role: "away" }),
      environment: {
        country: match?.stadium_country,
        stadium_id: match?.stadium_id,
        stadium_name: match?.stadium_name,
        stadium_address: match?.stadium_address,
        city: match?.stadium_city,
      }
    };
  });
}

function formatStatsAPIMatch({ match, role }) {
  const isHome = role === "home";

  return {
    team_id: isHome ? match?.home_team_id : match?.away_team_id,
    team_name: isHome ? match?.home_team_name : match?.away_team_name,
    role,
    team_three_letter_code: isHome ? match?.home_team_three_letter_code : match?.away_team_three_letter_code,
    team_short_name: isHome ? match?.home_team_short_name : match?.away_team_short_name,
    player_shirt_main_color: isHome ? match?.home_team_shirt_main_color : match?.away_team_shirt_main_color,
    player_shirt_secondary_color: isHome ? match?.home_team_shirt_secondary_color : match?.away_team_shirt_secondary_color,
    player_shirt_number_color: isHome ? match?.home_team_shirt_number_color : match?.away_team_shirt_number_color,
    player_shirt_type: isHome ? match?.home_team_shirt_type : match?.away_team_shirt_type,
    initial_line_up: isHome ? match?.home_team_initial_line_up : match?.away_team_initial_line_up,
    latest_line_up: isHome ? match?.home_team_latest_line_up : match?.away_team_latest_line_up,
  };
}

function formatStatsAPICompetition(match) {
  return {
    competition_id: match?.competition_id,
    competition_name: match?.competition_name,
    competition_type: match?.competition_type,
  };
}